import {
  Alert,
  AlertIcon,
  Box,
  BoxProps,
  Flex,
  FlexProps,
  Heading,
  Select,
  Tab,
  TabPanel,
  TabPanelProps,
  TabProps,
  Text,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { BLUE_COLOR, DARK_BLUE_COLOR, YELLOW_COLOR } from "../const";
import { SortByOptions, useSortByStore } from "../stores/SortByStore";
import { CaravanStockDTO } from "../types";
import { formatNumberWithPounds } from "../utils";
import { CaravanOverview } from "./CaravanOverview";

export const CaravanList: React.FC<{ caravanStock: CaravanStockDTO[] }> = ({ caravanStock }) => {
  const getParkText = useMemo(() => {
    const numberOfCaravans = caravanStock.length;
    const cheapestCaravanPrice = Math.min(...caravanStock.map((caravan) => caravan.price));

    const lowestPrice = formatNumberWithPounds(cheapestCaravanPrice);

    if (numberOfCaravans > 1) return `${numberOfCaravans} Holiday Homes found from ${lowestPrice}`;
    if (numberOfCaravans === 1) return `1 Holiday Home found for ${lowestPrice}`;
    return "0 Holiday Homes found";
  }, [caravanStock]);

  return (
    <>
      <Flex
        bg={YELLOW_COLOR}
        borderWidth={1}
        p={2}
        mb={6}
        alignItems="center"
        justify="space-between"
        flexDir={{ base: "column", md: "row" }}
        gap={{ base: 4, md: 2 }}
      >
        <Heading pl={2}>
          {getParkText}
        </Heading>

        <SortCaravansSelect />
      </Flex>
      <CaravanSort stock={caravanStock} />
    </>
  );
};

export const StyledTab: React.FC<TabProps> = (props) => (
  <Tab
    bg="gray.50"
    py={4}
    px={{ base: 2, md: 4 }}
    lineHeight="24px"
    color={BLUE_COLOR}
    _hover={{ color: DARK_BLUE_COLOR }}
    _selected={{ color: "white", bg: BLUE_COLOR }}
    {...props}
  />
);

export const SortCaravansSelect: React.FC<BoxProps> = (props) => {
  const { sortBy, setSortBy } = useSortByStore(["sortBy", "setSortBy"]);

  return (
    <Box {...props}>
      <Select
        borderRadius="20px"
        value={sortBy}
        onChange={(e) => setSortBy(e.target.value as SortByOptions)}
        bg="white"
      >
        <option value={SortByOptions.PriceDescending}>Price High to Low</option>
        <option value={SortByOptions.PriceAscending}>Price Low to High</option>
        <option value={SortByOptions.BedroomsDescending}>Bedrooms High to Low</option>
        <option value={SortByOptions.BedroomsAscending}>Bedrooms Low to High</option>
        <option value={SortByOptions.WidthDescending}>Width High to Low</option>
        <option value={SortByOptions.WidthAscending}>Width Low to High</option>
      </Select>
    </Box>
  );
};

export const CaravanSort: React.FC<{ stock: CaravanStockDTO[] } & FlexProps> = ({ stock, ...rest }) => {
  const { sortBy } = useSortByStore(["sortBy"]);

  const sortedCaravans = useMemo(() => {
    return stock.sort((a, b) => {
      switch (sortBy) {
        case SortByOptions.PriceAscending:
          return a.price - b.price;
        case SortByOptions.PriceDescending:
          return b.price - a.price;
        case SortByOptions.BedroomsAscending:
          return a.bedrooms - b.bedrooms;
        case SortByOptions.BedroomsDescending:
          return b.bedrooms - a.bedrooms;
        case SortByOptions.WidthAscending:
          return a.width - b.width;
        case SortByOptions.WidthDescending:
          return b.width - a.width;
        default:
          return a.price - b.price;
      }
    });
  }, [stock, sortBy]);

  return (
    <Flex flexDir="column" gap={4} {...rest}>
      {sortedCaravans.length === 0 && (
        <Alert status="warning">
          <AlertIcon />
          Sorry, we could not find any Caravans that match your search criteria at this park
        </Alert>
      )}
      {sortedCaravans.map((caravan) => (
        <CaravanOverview key={caravan.ID} caravan={caravan} />
      ))}
    </Flex>
  );
};

export const DesktopTabPanel: React.FC<{ showInCard: boolean; heading?: string } & TabPanelProps> = ({
  showInCard,
  heading,
  children,
  ...rest
}) => {
  // All have a heading except accommodation which has a unique layout
  if (showInCard)
    return (
      <TabPanel p={0} borderWidth={1} {...rest}>
        {heading && heading?.length > 0 && (
          <Box p={4} borderBottomWidth={1} bg="#fafafa">
            <Text fontSize="28px" lineHeight="35px" fontFamily="poppins, sans-serif">
              {heading}
            </Text>
          </Box>
        )}
        <Box p={4}>{children}</Box>
      </TabPanel>
    );
  return (
    <TabPanel p={0} {...rest}>
      {children}
    </TabPanel>
  );
};
